<template>
  <div class="debt-management">
    <div class="header">
      <h2>债务管理</h2>
      <div class="actions">
        <el-button type="primary" @click="handleAddDebt">
          <el-icon><Plus /></el-icon>
          添加债务
        </el-button>
        <el-button @click="refreshData">
          <el-icon><Refresh /></el-icon>
          刷新
        </el-button>
      </div>
    </div>

    <!-- 搜索和筛选 -->
    <el-card class="filter-card">
      <el-form :inline="true" :model="searchForm">
        <el-form-item label="债务人">
          <el-input v-model="searchForm.debtorName" placeholder="请输入债务人姓名" clearable />
        </el-form-item>
        <el-form-item label="债权人">
          <el-input v-model="searchForm.creditorName" placeholder="请输入债权人姓名" clearable />
        </el-form-item>
        <el-form-item label="债务状态">
          <el-select v-model="searchForm.status" placeholder="请选择状态" clearable>
            <el-option label="待审核" value="pending" />
            <el-option label="进行中" value="active" />
            <el-option label="已结清" value="settled" />
            <el-option label="逾期" value="overdue" />
            <el-option label="争议中" value="disputed" />
          </el-select>
        </el-form-item>
        <el-form-item label="创建时间">
          <el-date-picker
            v-model="searchForm.dateRange"
            type="daterange"
            range-separator="至"
            start-placeholder="开始日期"
            end-placeholder="结束日期"
            value-format="YYYY-MM-DD"
          />
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="handleSearch">
            <el-icon><Search /></el-icon>
            搜索
          </el-button>
          <el-button @click="resetSearch">
            <el-icon><Refresh /></el-icon>
            重置
          </el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <!-- 债务列表 -->
    <el-card>
      <el-table :data="debtList" v-loading="loading" stripe border>
        <el-table-column prop="id" label="ID" width="80" />
        <el-table-column prop="debtorName" label="债务人" width="120" />
        <el-table-column prop="creditorName" label="债权人" width="120" />
        <el-table-column prop="amount" label="金额" width="120">
          <template #default="{ row }">
            ¥{{ formatAmount(row.amount) }}
          </template>
        </el-table-column>
        <el-table-column prop="interestRate" label="利率" width="100">
          <template #default="{ row }">
            {{ row.interestRate }}%
          </template>
        </el-table-column>
        <el-table-column prop="dueDate" label="到期日" width="120">
          <template #default="{ row }">
            {{ formatDate(row.dueDate) }}
          </template>
        </el-table-column>
        <el-table-column prop="status" label="状态" width="100">
          <template #default="{ row }">
            <el-tag :type="getStatusTag(row.status)">
              {{ getStatusText(row.status) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="progress" label="还款进度" width="150">
          <template #default="{ row }">
            <el-progress :percentage="row.progress" :status="getProgressStatus(row.progress)" />
          </template>
        </el-table-column>
        <el-table-column prop="createdAt" label="创建时间" width="180">
          <template #default="{ row }">
            {{ formatDateTime(row.createdAt) }}
          </template>
        </el-table-column>
        <el-table-column label="操作" width="250" fixed="right">
          <template #default="{ row }">
            <el-button type="primary" size="small" @click="handleView(row)">
              查看
            </el-button>
            <el-button type="warning" size="small" @click="handleEdit(row)">
              编辑
            </el-button>
            <el-button 
              v-if="row.status === 'pending'" 
              type="success" 
              size="small" 
              @click="handleApprove(row)"
            >
              审核通过
            </el-button>
            <el-button 
              v-if="row.status === 'pending'" 
              type="danger" 
              size="small" 
              @click="handleReject(row)"
            >
              审核拒绝
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <div class="pagination">
        <el-pagination
          v-model:current-page="pagination.current"
          v-model:page-size="pagination.size"
          :total="pagination.total"
          :page-sizes="[10, 20, 50, 100]"
          layout="total, sizes, prev, pager, next, jumper"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
        />
      </div>
    </el-card>

    <!-- 查看详情对话框 -->
    <el-dialog
      v-model="viewDialogVisible"
      title="债务详情"
      width="700px"
    >
      <el-descriptions :column="2" border v-if="currentDebt">
        <el-descriptions-item label="债务ID">{{ currentDebt.id }}</el-descriptions-item>
        <el-descriptions-item label="状态">
          <el-tag :type="getStatusTag(currentDebt.status)">
            {{ getStatusText(currentDebt.status) }}
          </el-tag>
        </el-descriptions-item>
        <el-descriptions-item label="债务人">{{ currentDebt.debtorName }}</el-descriptions-item>
        <el-descriptions-item label="债务人ID">{{ currentDebt.debtorId }}</el-descriptions-item>
        <el-descriptions-item label="债权人">{{ currentDebt.creditorName }}</el-descriptions-item>
        <el-descriptions-item label="债权人ID">{{ currentDebt.creditorId }}</el-descriptions-item>
        <el-descriptions-item label="债务金额">
          <span class="amount-highlight">¥{{ formatAmount(currentDebt.amount) }}</span>
        </el-descriptions-item>
        <el-descriptions-item label="利率">{{ currentDebt.interestRate }}%</el-descriptions-item>
        <el-descriptions-item label="到期日期">{{ formatDate(currentDebt.dueDate) }}</el-descriptions-item>
        <el-descriptions-item label="还款进度">
          <el-progress :percentage="currentDebt.progress" :status="getProgressStatus(currentDebt.progress)" />
        </el-descriptions-item>
        <el-descriptions-item label="已还金额">¥{{ formatAmount(currentDebt.paidAmount || 0) }}</el-descriptions-item>
        <el-descriptions-item label="剩余金额">¥{{ formatAmount(currentDebt.amount - (currentDebt.paidAmount || 0)) }}</el-descriptions-item>
        <el-descriptions-item label="创建时间" :span="2">{{ formatDateTime(currentDebt.createdAt) }}</el-descriptions-item>
        <el-descriptions-item label="更新时间" :span="2">{{ formatDateTime(currentDebt.updatedAt) || '-' }}</el-descriptions-item>
        <el-descriptions-item label="债务描述" :span="2">{{ currentDebt.description || '无' }}</el-descriptions-item>
        <el-descriptions-item label="备注" :span="2">{{ currentDebt.remarks || '无' }}</el-descriptions-item>
      </el-descriptions>
      <template #footer>
        <el-button @click="viewDialogVisible = false">关闭</el-button>
        <el-button type="primary" @click="handleEdit(currentDebt); viewDialogVisible = false">编辑</el-button>
      </template>
    </el-dialog>

    <!-- 编辑债务对话框 -->
    <el-dialog
      v-model="editDialogVisible"
      title="编辑债务"
      width="600px"
    >
      <el-form ref="editFormRef" :model="editForm" :rules="editRules" label-width="100px">
        <el-form-item label="债务状态" prop="status">
          <el-select v-model="editForm.status" placeholder="请选择状态">
            <el-option label="待审核" value="pending" />
            <el-option label="进行中" value="active" />
            <el-option label="已结清" value="settled" />
            <el-option label="逾期" value="overdue" />
            <el-option label="争议中" value="disputed" />
          </el-select>
        </el-form-item>
        <el-form-item label="债务金额" prop="amount">
          <el-input v-model="editForm.amount" type="number" :min="0" step="0.01">
            <template #append>元</template>
          </el-input>
        </el-form-item>
        <el-form-item label="利率" prop="interestRate">
          <el-input v-model="editForm.interestRate" type="number" :min="0" :max="100" step="0.1">
            <template #append>%</template>
          </el-input>
        </el-form-item>
        <el-form-item label="到期日期" prop="dueDate">
          <el-date-picker
            v-model="editForm.dueDate"
            type="date"
            placeholder="请选择到期日期"
            value-format="YYYY-MM-DD"
          />
        </el-form-item>
        <el-form-item label="还款进度" prop="progress">
          <el-slider v-model="editForm.progress" :min="0" :max="100" show-input />
        </el-form-item>
        <el-form-item label="已还金额" prop="paidAmount">
          <el-input v-model="editForm.paidAmount" type="number" :min="0" step="0.01">
            <template #append>元</template>
          </el-input>
        </el-form-item>
        <el-form-item label="债务描述" prop="description">
          <el-input v-model="editForm.description" type="textarea" :rows="3" placeholder="请输入债务描述" />
        </el-form-item>
        <el-form-item label="备注" prop="remarks">
          <el-input v-model="editForm.remarks" type="textarea" :rows="2" placeholder="请输入备注" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="editDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleSaveEdit" :loading="saving">保存</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Plus, Refresh, Search } from '@element-plus/icons-vue'
import { debtApi } from '@/api/modules/debt.js'

// 状态管理
const loading = ref(false)
const viewDialogVisible = ref(false)
const editDialogVisible = ref(false)
const saving = ref(false)
const currentDebt = ref(null)
const editFormRef = ref()

// 债务列表数据
const debtList = ref([])

// 搜索表单
const searchForm = reactive({
  debtorName: '',
  creditorName: '',
  status: '',
  dateRange: []
})

// 编辑表单
const editForm = reactive({
  id: null,
  status: '',
  amount: 0,
  interestRate: 0,
  dueDate: '',
  progress: 0,
  paidAmount: 0,
  description: '',
  remarks: ''
})

// 编辑表单验证规则
const editRules = {
  status: [{ required: true, message: '请选择状态', trigger: 'change' }],
  amount: [{ required: true, message: '请输入债务金额', trigger: 'blur' }],
  interestRate: [{ required: true, message: '请输入利率', trigger: 'blur' }],
  dueDate: [{ required: true, message: '请选择到期日期', trigger: 'change' }]
}

// 分页配置
const pagination = reactive({
  current: 1,
  size: 10,
  total: 0
})

// 获取债务列表
const fetchDebts = async () => {
  try {
    loading.value = true
    const response = await debtApi.getDebts({
      page: pagination.current,
      size: pagination.size,
      ...searchForm
    })
    if (response.code === 200 && response.data) {
      debtList.value = response.data.list
      pagination.total = response.data.total
    } else {
      if (response.code === 401) {
        localStorage.removeItem('admin-token')
        localStorage.removeItem('admin-info')
        window.location.href = '/#/login'
        return
      } else {
        ElMessage.error(response.message || '获取债务列表失败')
      }
    }
  } catch (error) {
    ElMessage.error('获取债务列表异常')
  } finally {
    loading.value = false
  }
}

// 方法
const getStatusText = (status) => {
  const map = {
    pending: '待审核',
    active: '进行中',
    settled: '已结清',
    overdue: '逾期',
    disputed: '争议中'
  }
  return map[status] || status
}

const getStatusTag = (status) => {
  const map = {
    pending: 'warning',
    active: 'success',
    settled: 'info',
    overdue: 'danger',
    disputed: ''
  }
  return map[status] || ''
}

const getProgressStatus = (progress) => {
  if (progress >= 100) return 'success'
  if (progress >= 70) return ''
  if (progress >= 30) return 'warning'
  return 'exception'
}

const formatAmount = (amount) => {
  if (!amount) return '0.00'
  return parseFloat(amount).toLocaleString('zh-CN', { minimumFractionDigits: 2 })
}

const formatDate = (dateString) => {
  if (!dateString) return ''
  return dateString
}

const formatDateTime = (dateString) => {
  if (!dateString) return ''
  const date = new Date(dateString)
  return date.toLocaleString('zh-CN')
}

const refreshData = () => {
  fetchDebts()
}

const handleSearch = () => {
  pagination.current = 1
  fetchDebts()
}

const resetSearch = () => {
  Object.keys(searchForm).forEach(key => {
    if (key === 'dateRange') {
      searchForm[key] = []
    } else {
      searchForm[key] = ''
    }
  })
  handleSearch()
}

const handleSizeChange = (size) => {
  pagination.size = size
  fetchDebts()
}

const handleCurrentChange = (page) => {
  pagination.current = page
  fetchDebts()
}

const handleAddDebt = async () => {
  try {
    const debtorResult = await ElMessageBox.prompt('请输入债务人ID', '添加债务', {
      confirmButtonText: '下一步',
      cancelButtonText: '取消',
      inputPattern: /^\d+$/,
      inputErrorMessage: '债务人ID必须为数字'
    })
    if (debtorResult.action !== 'confirm') return
    const debtorId = parseInt(debtorResult.value)
    
    const creditorResult = await ElMessageBox.prompt('请输入债权人ID', '添加债务', {
      confirmButtonText: '下一步',
      cancelButtonText: '上一步',
      inputPattern: /^\d+$/,
      inputErrorMessage: '债权人ID必须为数字'
    })
    if (creditorResult.action !== 'confirm') return
    const creditorId = parseInt(creditorResult.value)
    
    const amountResult = await ElMessageBox.prompt('请输入债务金额（元）', '添加债务', {
      confirmButtonText: '提交',
      cancelButtonText: '上一步',
      inputPattern: /^\d+(\.\d{1,2})?$/,
      inputErrorMessage: '金额必须为数字，最多两位小数'
    })
    if (amountResult.action !== 'confirm') return
    const amount = parseFloat(amountResult.value)
    
    const response = await debtApi.createDebt({
      debtorId,
      creditorId,
      amount,
      status: 'pending'
    })
    
    if (response.code === 200 || response.code === 201) {
      ElMessage.success('债务添加成功')
      fetchDebts()
    } else {
      ElMessage.error(response.message || '债务添加失败')
    }
  } catch (error) {
    if (error !== 'cancel' && error !== 'close') {
      ElMessage.error('添加债务失败')
    }
  }
}

const handleView = (row) => {
  currentDebt.value = row
  viewDialogVisible.value = true
}

const handleEdit = (row) => {
  currentDebt.value = row
  editForm.id = row.id
  editForm.status = row.status
  editForm.amount = row.amount
  editForm.interestRate = row.interestRate || 0
  editForm.dueDate = row.dueDate
  editForm.progress = row.progress || 0
  editForm.paidAmount = row.paidAmount || 0
  editForm.description = row.description || ''
  editForm.remarks = row.remarks || ''
  editDialogVisible.value = true
}

const handleSaveEdit = async () => {
  if (!editFormRef.value) return
  
  try {
    await editFormRef.value.validate()
    saving.value = true
    
    const response = await debtApi.updateDebt(editForm.id, {
      status: editForm.status,
      amount: parseFloat(editForm.amount),
      interestRate: parseFloat(editForm.interestRate),
      dueDate: editForm.dueDate,
      progress: editForm.progress,
      paidAmount: parseFloat(editForm.paidAmount),
      description: editForm.description,
      remarks: editForm.remarks
    })
    
    if (response.code === 200) {
      ElMessage.success('债务更新成功')
      editDialogVisible.value = false
      fetchDebts()
    } else {
      ElMessage.error(response.message || '更新失败')
    }
  } catch (error) {
    console.log('表单验证失败:', error)
  } finally {
    saving.value = false
  }
}

const handleApprove = async (row) => {
  try {
    await ElMessageBox.confirm('确定要审核通过这笔债务吗？', '审核确认', {
      confirmButtonText: '通过',
      cancelButtonText: '取消',
      type: 'warning'
    })
    
    const response = await debtApi.updateDebtStatus(row.id, 'active')
    if (response.code === 200) {
      row.status = 'active'
      ElMessage.success('债务审核通过')
      fetchDebts()
    } else {
      ElMessage.error(response.message || '审核失败')
    }
  } catch (error) {
    if (error !== 'cancel') {
      ElMessage.error('审核失败')
    }
  }
}

const handleReject = async (row) => {
  try {
    await ElMessageBox.confirm('确定要拒绝这笔债务吗？', '拒绝确认', {
      confirmButtonText: '拒绝',
      cancelButtonText: '取消',
      type: 'warning'
    })
    
    const response = await debtApi.deleteDebt(row.id)
    if (response.code === 200) {
      ElMessage.success('债务已拒绝')
      fetchDebts()
    } else {
      ElMessage.error(response.message || '拒绝失败')
    }
  } catch (error) {
    if (error !== 'cancel') {
      ElMessage.error('拒绝失败')
    }
  }
}

// 生命周期
onMounted(() => {
  fetchDebts()
})
</script>

<style scoped>
.debt-management {
  padding: 20px;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.header h2 {
  margin: 0;
  font-size: 24px;
  color: #ffffff;
}

.actions {
  display: flex;
  gap: 10px;
}

.filter-card {
  margin-bottom: 20px;
}

.pagination {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
}

.amount-highlight {
  font-size: 18px;
  font-weight: bold;
  color: #409eff;
}
</style>
